import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatINRFull, formatStatus, REVENUE_PAYMENT_MODES, formatDate } from '@/lib/utils';
import { Plus, Trash2 } from 'lucide-react';

export default function RevenuePage() {
  const { user } = useAuth();
  const [revenues, setRevenues] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterProject, setFilterProject] = useState('');

  const fetchRevenue = () => {
    const params = new URLSearchParams();
    if (filterProject) params.set('project_id', filterProject);
    api.get(`/revenue?${params}`).then(r => setRevenues(r.data.revenues || [])).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchRevenue(); }, [filterProject]);
  useEffect(() => { api.get('/projects').then(r => setProjects(r.data.projects || [])); }, []);

  const canCreate = ['DIRECTOR', 'ACCOUNTANT'].includes(user?.role);
  const totalRevenue = revenues.reduce((s, r) => s + (r.amount || 0), 0);

  const deleteRevenue = async (id) => {
    if (!window.confirm('Delete this revenue entry?')) return;
    await api.delete(`/revenue/${id}`);
    fetchRevenue();
  };

  return (
    <div data-testid="revenue-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Revenue / Payments</h1>
          <p className="text-sm text-slate-500">{revenues.length} entries | Total: {formatINRFull(totalRevenue)}</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate(true)} data-testid="add-revenue-button"
            className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
            <Plus size={16} /> Record Payment
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
        </select>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div> : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead><tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Project</th>
              <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Amount</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Mode</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Invoice</th>
              <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Remarks</th>
              {canCreate && <th className="w-10"></th>}
            </tr></thead>
            <tbody>
              {revenues.map(r => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2.5 px-4 text-slate-500">{formatDate(r.date)}</td>
                  <td className="py-2.5 px-4 text-xs font-medium text-[#1B5486]">{r.project_code || '-'}</td>
                  <td className="py-2.5 px-4 text-right font-mono font-medium text-emerald-600">{formatINRFull(r.amount)}</td>
                  <td className="py-2.5 px-4"><span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{r.payment_mode}</span></td>
                  <td className="py-2.5 px-4 text-slate-700">{r.invoice_number || '-'}</td>
                  <td className="py-2.5 px-4 text-slate-500">{r.remarks}</td>
                  {canCreate && <td className="py-2.5 px-2"><button onClick={() => deleteRevenue(r.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {revenues.length === 0 && <div className="text-center py-12 text-slate-400">No revenue entries</div>}
        </div>
      )}

      {showCreate && <CreateRevenueModal projects={projects} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchRevenue(); }} />}
    </div>
  );
}

function CreateRevenueModal({ projects, onClose, onCreated }) {
  const [form, setForm] = useState({
    project_id: '', date: new Date().toISOString().slice(0, 10),
    amount: '', payment_mode: 'NEFT', invoice_number: '', invoice_id: '', remarks: ''
  });
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (form.project_id) {
      api.get(`/po-invoices/invoices?project_id=${form.project_id}`).then(r => setInvoices(r.data.invoices || [])).catch(() => setInvoices([]));
    } else { setInvoices([]); }
  }, [form.project_id]);

  const handleInvoiceSelect = (invoiceId) => {
    const inv = invoices.find(i => i.id === invoiceId);
    setForm({...form, invoice_id: invoiceId, invoice_number: inv ? inv.invoice_number : ''});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/revenue', form);
      // D16: Auto-update invoice status to PAYMENT_RECEIVED
      if (form.invoice_id) {
        try { await api.patch(`/po-invoices/invoices/${form.invoice_id}`, { status: 'PAYMENT_RECEIVED', payment_amount: parseFloat(form.amount) || 0, payment_date: form.date }); } catch (err) { console.warn('Invoice status update failed:', err); }
      }
      onCreated();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg p-6" onClick={e => e.stopPropagation()} data-testid="create-revenue-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">Record Payment</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Project *</label>
            <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value, invoice_id: '', invoice_number: ''})} required className="input-field w-full">
              <option value="">Select project</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
            </select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
              <input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required className="input-field w-full" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
              <select value={form.payment_mode} onChange={e => setForm({...form, payment_mode: e.target.value})} className="input-field w-full">
                {REVENUE_PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}</select></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Invoice</label>
              {invoices.length > 0 ? (
                <select value={form.invoice_id} onChange={e => handleInvoiceSelect(e.target.value)} className="input-field w-full" data-testid="revenue-invoice-dropdown">
                  <option value="">Select Invoice</option>
                  {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.invoice_number} - {formatINRFull(inv.total_amount)} ({formatStatus(inv.status)})</option>)}
                </select>
              ) : (
                <input value={form.invoice_number} onChange={e => setForm({...form, invoice_number: e.target.value})} className="input-field w-full" placeholder="Invoice number" />
              )}
            </div>
          </div>
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Remarks</label>
            <input value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} className="input-field w-full" placeholder="e.g., Advance, Milestone 1" /></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Saving...' : 'Record Payment'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
